/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import DateFnsAdapter from '@date-io/date-fns';
import { withTranslation } from 'config/i18n';
import { makeStyles, alpha } from '@material-ui/core';
import clsx from 'clsx';
import ERTable from 'components/shared/ERTable';
import Dinero from 'dinero.js';
import Link from 'components/shared/Link';
import TablePagination from '@material-ui/core/TablePagination';

const useStyles = makeStyles((theme) => ({
  root: {},
  capitalize: {
    textTransform: 'capitalize',
  },
  amountText: {
    color: theme.palette.common.black,
    fontSize: theme.typography.pxToRem(16),
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
  },
  italic: {
    fontStyle: 'italic',
  },
  bold: {
    fontWeight: 700,
  },
  underlineText: {
    textDecoration: 'underline',
  },
}));

const TransactionHistoryTable = ({
  t,
  data,
  page,
  rowsPerPage,
  count,
  onChangePage,
  onChangeRowsPerPage,
}) => {
  const classes = useStyles();

  const dateFns = new DateFnsAdapter();

  const [rows, setRows] = useState([]);

  const [columns] = useState([
    <>{t('fe_earnings_and_payouts_page:transaction_history.table.date')}</>,
    <>{t('fe_earnings_and_payouts_page:transaction_history.table.type')}</>,
    <>{t('fe_earnings_and_payouts_page:transaction_history.table.status')}</>,
    <>{t('fe_earnings_and_payouts_page:transaction_history.table.details')}</>,
    <>{t('fe_earnings_and_payouts_page:transaction_history.table.deposit')}</>,
    <>{t('fe_earnings_and_payouts_page:transaction_history.table.withdrawal')}</>,
  ]);

  const formatPrice = (value) => `${value.currency_code || ''}${Dinero({
    amount: value.amount || 0,
  }).toFormat('0,0.00')}`;

  const getConfirmationLink = (confirmationNumber, id) => {
    if (!confirmationNumber || !id) return '';
    return (
      <Link
        target="_blank"
        rel="noreferrer"
        color="secondary"
        href={`/vehicle_reservations/${id}`}
        className={(classes.bold, classes.underlineText)}
      >
        {confirmationNumber}
      </Link>
    );
  };

  const getBikeLink = (vehicle) => {
    if (!vehicle || !vehicle.make || !vehicle.model || !vehicle.geo) return '';
    const year = vehicle.year || '';
    const make_name = vehicle.make.name || '';
    const model_name = vehicle.model.name || '';
    const vehicleText = `${year} ${make_name} ${model_name}`;
    return (
      <Link
        color="secondary"
        target="_blank"
        rel="noreferrer"
        className={(classes.bold, classes.underlineText)}
        href={{
          pathname: `/${vehicle.make.slug}/${vehicle.model.slug}/${vehicle.geo.slug}/[id]`,
          query: { id: vehicle.id },
        }}
        as={`/${vehicle.make.slug}/${vehicle.model.slug}/${vehicle.geo.slug}/${vehicle.id}`}
      >
        (
        {vehicleText}
        )
      </Link>
    );
  };

  useEffect(() => {
    const _rows = [];
    if (data?.transactions) {
      data.transactions.forEach((row) => {
        _rows.push([
          <span>
            {dateFns.isValid(new Date(row.transaction_time))
              ? dateFns.format(new Date(row.transaction_time), 'MMM dd, yyyy')
              : ''}
          </span>,
          <>
            {row.transaction_type
              ? row.transaction_type == 'host_transfer'
                ? 'Transfer'
                : 'Booking'
              : ''}
          </>,
          <span
            className={clsx(classes.capitalize, {
              [classes.italic]: row.transaction_status === 'in_process',
            })}
          >
            {row.transaction_status?.replace('_', ' ') || ''}
          </span>,
          <>
            {row.transaction_type && (
              <>
                {row.transaction_type === 'host_earnings' && row.details && (
                  <>
                    {t('fe_earnings_and_payouts_page:transaction_history.table.earnings_from')}
                    &nbsp;
                    {(row.details.vehicle_reservation
                      && getConfirmationLink(
                        row.details.vehicle_reservation.confirmation_number,
                        row.details.vehicle_reservation.id,
                      ))
                      || ''}
                    <br />
                    {(row.details.vehicle && getBikeLink(row.details.vehicle)) || ''}
                  </>
                )}
                {row.transaction_type === 'host_penalty' && row.details && (
                  <>
                    {t('fe_earnings_and_payouts_page:transaction_history.table.penalty_from')}
                    &nbsp;
                    {(row.details.vehicle_reservation
                      && getConfirmationLink(
                        row.details.vehicle_reservation.confirmation_number,
                        row.details.vehicle_reservation.id,
                      ))
                      || ''}
                    <br />
                    {(row.details.vehicle && getBikeLink(row.details.vehicle)) || ''}
                  </>
                )}
                {row.transaction_type === 'host_transfer'
                  && row.details
                  && t(
                    'fe_earnings_and_payouts_page:transaction_history.table.transfer_to_external_account',
                  )}
              </>
            )}
          </>,
          <span className={clsx(classes.amountText, classes.bold)}>
            {row.transaction_type == 'host_earnings' && row.amount && (
              <>{`+ ${formatPrice(row.amount)}`}</>
            )}
          </span>,
          <span className={classes.bold}>
            {row.transaction_type != 'host_earnings' && row.amount && (
              <>{`- ${formatPrice(row.amount)}`}</>
            )}
          </span>,
        ]);
      });
    }
    setRows(_rows);
  }, [data]);

  return (
    <div className={classes.root}>
      <ERTable rows={rows} columns={columns} />
      <TablePagination
        rowsPerPageOptions={[5, 10, 20]}
        component="div"
        count={count}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={onChangePage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        labelRowsPerPage={t('fe_pagination:rows_per_page')}
        nextIconButtonText={t('fe_pagination:next_page')}
        backIconButtonText={t('fe_pagination:previous_page')}
      />
    </div>
  );
};

export default withTranslation(['fe_earnings_and_payouts_page', 'fe_pagination'])(
  TransactionHistoryTable,
);
